import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { EMPTY_LIST } from '../../../constants/common'
import { useInitData, useNotification } from '../../../hooks'
import { getBudgetPlanProfilePathname } from '../../../utils/link'
import { FormikForm } from '../../Formik'
import {
  applyFieldDepends,
  getInvoiceModalInitialValues,
  getInvoiceModalValidationSchema,
  getNormalizedInvoiceSubmitValues,
} from '../InvoiceModal.utils'

import { FormFields } from './FormFields'

function InvoiceModalForm({
  FormContainer,
  containerProps,
  documentId,
  fields,
  requestId,
  savedDocumentId,
  submitTitleKey,
  successTitleKey,
  withBudgetPlanNavigation = false,
  withFileUpload = false,
  onCancel,
  onClose,
  onSubmitRequest,
  onSuccess,
}) {
  const { t } = useTranslation()

  const { availableSections } = useInitData()

  const { handleAddSuccessNotification } = useNotification()

  const navigate = useNavigate()

  const initialDocumentId = documentId ?? null

  const [currentDocumentId, setCurrentDocumentId] = useState(initialDocumentId)

  const isFileChanged = currentDocumentId !== initialDocumentId

  const changedFiles = isFileChanged ? [currentDocumentId] : EMPTY_LIST

  const initialValues = getInvoiceModalInitialValues(fields)
  const validationSchema = getInvoiceModalValidationSchema(fields, t)

  function handleSubmit(values, { setSubmitting }) {
    const processedFields = fields.map(f => applyFieldDepends(f, values))
    const normalizedFields = getNormalizedInvoiceSubmitValues(
      values,
      initialValues,
      processedFields
    )

    onSubmitRequest({
      fields: normalizedFields,
      invoiceFileId: currentDocumentId,
      isFileChanged,
    })
      .then(({ data }) => {
        onSuccess?.()
        onClose()

        handleAddSuccessNotification({
          title: t(successTitleKey),
        })

        if (withBudgetPlanNavigation && data?.budget_plan) {
          navigate(
            getBudgetPlanProfilePathname({
              availableSections,
              id: data.budget_plan,
            })
          )
        }
      })
      .catch(() => {
        setSubmitting(false)
      })
  }

  return (
    <FormikForm
      Container={FormContainer}
      containerProps={{ ...containerProps, files: changedFiles }}
      initialValues={initialValues}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <FormFields
        documentId={currentDocumentId}
        fields={fields}
        files={changedFiles}
        isFileChanged={isFileChanged}
        requestId={requestId}
        savedDocumentId={savedDocumentId}
        submitTitleKey={submitTitleKey}
        withFileUpload={withFileUpload}
        onCancel={onCancel}
        onDocumentIdChange={setCurrentDocumentId}
      />
    </FormikForm>
  )
}

export default InvoiceModalForm
